import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import useResponsive from '../../../hooks/useResponsive';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import { getNavConfig } from './config';
import { getLogoutPayload, getUserInfo } from '../../../utils/authUtil';
import { useNavigate } from 'react-router-dom';
import API from '../../../data/api';
import { useAuth } from '../../../contexts/AuthContext';
import { StyledNavItemIcon } from 'src/components/nav-section/styles';
import SvgColor from 'src/components/svg-color';

const NAV_WIDTH = 280;
const NAV_COLLAPSED_WIDTH = 88;

const PLAN_TIERS = [
  {
    name: 'ERP Pro',
    price: '$50 per month',
    users: '1 user',
    features: ['Unlimited data ingestion', 'Reporting', 'Data analysis'],
  },
  {
    name: 'ERP Team',
    price: '$15 per user',
    users: 'Up to 100 users',
    features: ['Machine learning models enabled', 'System alerts (email)', 'Staff KPIs (petrol attendants)'],
  },
  {
    name: 'ERP Business',
    price: '$24 per user',
    users: 'No user cap',
    features: [
      'Twilio updates (WhatsApp, SMS)',
      'Machine learning models',
      'Payslips (petrol attendants)',
      'RBAC implementation',
      'Link your bank account into HoneyBadger',
      'Ledger accounting',
    ],
  },
];

const normalizeTier = (value) => {
  if (!value) return '';
  const normalized = String(value).trim().toLowerCase();
  if (normalized.includes('business')) return 'ERP Business';
  if (normalized.includes('team')) return 'ERP Team';
  if (normalized.includes('pro')) return 'ERP Pro';
  return '';
};

const resolveCurrentTier = (userInfo) => {
  const localTier = localStorage.getItem('erp_tier') || localStorage.getItem('subscription_tier');
  const candidates = [
    userInfo?.erp_tier,
    userInfo?.subscription_tier,
    userInfo?.plan_tier,
    userInfo?.plan,
    userInfo?.tier,
    localTier,
  ];
  for (const candidate of candidates) {
    const resolved = normalizeTier(candidate);
    if (resolved) {
      return resolved;
    }
  }
  return 'ERP Pro';
};

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

Nav.propTypes = {
  isCollapsed: PropTypes.bool,
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav, isCollapsed = false }) {

  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const isDesktop = useResponsive('up', 'lg');
  const useCollapsedNav = isDesktop && isCollapsed;
  const [userInfo, setUserInfo] = useState({});
  const [openPlanModal, setOpenPlanModal] = useState(false);
  const navConfig = getNavConfig(userInfo);
  const currentTier = useMemo(() => resolveCurrentTier(userInfo), [userInfo]);

  useEffect(() => {

  }, [isDesktop, openNav]);

  useEffect(() => {
    const storedUserInfo = getUserInfo();
    if (storedUserInfo) {
      // const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(storedUserInfo);
    }
  }, []);

  // TODO: merge with other logout method
  const handleLogout = async () => {
    try {
      const response = await API.post('/auth/logout', getLogoutPayload());

      if (response.status === 200) {
        // Clear local storage
        logout();

        // Navigate to login page or home page
        navigate('/login');
      } else {
        console.error('Logout failed:', response);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          margin: useCollapsedNav ? '16px auto' : 3,
          width: useCollapsedNav ? '42px' : '80px',
          transition: theme.transitions.create(['width', 'margin'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <img
          // src="https://firebasestorage.googleapis.com/v0/b/los-diesel.appspot.com/o/assets%2FLD%20Horizontal%20logo%20BLue.svg?alt=media&token=05cda179-e6bf-46fd-b78c-968183558b8e"
          alt="Honye Badger"
          src="/assets/images/hb-logo-only-light.png"
        />
      </Box>

      <Box sx={{ mb: 2, mx: 1, display: useCollapsedNav ? 'none' : 'block' }}>
        <Link underline="none">
          <StyledAccount
            sx={{
              color: theme.palette.common.white,
              background: 'radial-gradient(farthest-side at 80%, #828282 0%, transparent 20%)',
              bgcolor: 'action.selected',
              fontWeight: 'fontWeightBold',
            }}
          >
            <Avatar src={userInfo.photoURL} alt="photoURL" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: theme.palette.common.white }}>
                {userInfo.given_name} {userInfo.family_name}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.common.white }}>
                {userInfo.position || 'User'}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navConfig} isCollapsed={useCollapsedNav} />

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', justifyContent: 'center' }}>
        {useCollapsedNav ? (
          <Tooltip title="Upgrade Plan" placement="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenPlanModal(true)}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: 1.25,
                p: 0,
              }}
            >
              <WorkspacePremiumIcon fontSize="small" />
            </Button>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<WorkspacePremiumIcon />}
            onClick={() => setOpenPlanModal(true)}
            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
          >
            Upgrade Plan
          </Button>
        )}
      </Box>
      <Divider />
      {useCollapsedNav ? (
        <Tooltip title="Logout" placement="right">
          <MenuItem
            onClick={handleLogout}
            sx={{
              ml: 0,
              my: 1.5,
              color: theme.palette.grey[500],
              justifyContent: 'center',
            }}
          >
            <StyledNavItemIcon sx={{ minWidth: 'auto' }}>
              <SvgColor src={`/assets/icons/navbar/ic_logout.svg`} sx={{ width: 1, height: 1 }} />
            </StyledNavItemIcon>
          </MenuItem>
        </Tooltip>
      ) : (
        <MenuItem
          onClick={handleLogout}
          sx={{
            ml: 0,
            my: 1.5,
            color: theme.palette.grey[500],
            justifyContent: 'flex-start',
          }}
        >
          <StyledNavItemIcon sx={{ minWidth: 0 }}>
            <SvgColor src={`/assets/icons/navbar/ic_logout.svg`} sx={{ width: 1, height: 1 }} />
          </StyledNavItemIcon>
          <Typography sx={{ ml: 1 }}>Logout</Typography>
        </MenuItem>
      )}
    </Scrollbar>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          flexShrink: { lg: 0 },
          width: { lg: useCollapsedNav ? NAV_COLLAPSED_WIDTH : NAV_WIDTH },
          color: theme.palette.common.white,
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {isDesktop ? (
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                width: useCollapsedNav ? NAV_COLLAPSED_WIDTH : NAV_WIDTH,
                backgroundColor: theme.palette.custom.drawerBg,
                color: theme.palette.common.white,
                borderRightStyle: 'dashed',
                transition: theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
              },
            }}
          >
            {renderContent}
          </Drawer>
        ) : (
          <Drawer
            open={openNav}
            onClose={onCloseNav}
            slotProps={{
              backdrop: { style: { backgroundColor: "transparent" } },
            }}
            PaperProps={{
              sx: {
                marginTop: 6,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.custom.drawerBg,
              },
            }}
          >
            <Box
              sx={{ width: 210, }}
              onClick={onCloseNav}
            >
              {renderContent}
            </Box>
          </Drawer>
        ) }
      </Box>

      <Dialog open={openPlanModal} onClose={() => setOpenPlanModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Upgrade Plan</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current tier: <strong>{currentTier}</strong>
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {PLAN_TIERS.map((tier) => {
              const isCurrent = tier.name === currentTier;
              return (
                <Paper
                  key={tier.name}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    backgroundColor: isCurrent ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                    height: '100%',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{tier.name}</Typography>
                      {isCurrent && <Chip size="small" label="Current Tier" color="primary" />}
                    </Stack>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {tier.price}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tier.users}
                    </Typography>
                    {tier.features.map((feature) => (
                      <Typography variant="body2" key={`${tier.name}-${feature}`}>
                        - {feature}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
